import { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { MdPalette } from 'react-icons/md';

import { Line } from '../../../types/Line';
import * as S from './styles';

interface ISidebarLinesProps {
  lines: Line[];
  onToggle: (id: number) => void;
  selectedIds: number[];
  lineColors: { [key: number]: string };
  onColorChange: (id: number, color: string) => void;
  showInactiveLineIds: number[];
  onToggleInactive: (id: number) => void;
}

const SidebarLines = ({
  lines,
  onToggle,
  selectedIds,
  lineColors,
  onColorChange,
  showInactiveLineIds,
  onToggleInactive,
}: ISidebarLinesProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLines = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return lines.filter(
      line =>
        line.DFNOMELINHA?.toLowerCase().includes(term) ||
        line.DFIDLINHA?.toString().includes(term),
    );
  }, [lines, searchTerm]);

  return (
    <S.SidebarContainer>
      <S.SidebarHeader>
        <span>Linhas Cadastradas</span>
        <span>{selectedIds.length} selecionadas</span>
      </S.SidebarHeader>

      <S.SearchWrapper>
        <S.SearchInputContainer>
          <S.SearchInput
            type="text"
            placeholder="Buscar linha ou código..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <FiSearch size={16} />
        </S.SearchInputContainer>
      </S.SearchWrapper>

      <S.LinesList>
        {filteredLines.length > 0 ? (
          filteredLines.map(line => {
            const lineId = Number(line.DFIDLINHA);
            const isSelected = selectedIds.includes(lineId);
            const showInactive = showInactiveLineIds.includes(lineId);
            const lineColor = lineColors[lineId] || '#007cbf';

            return (
              <S.LineItem
                key={line.DFIDLINHA}
                isSelected={isSelected}
                lineColor={lineColor}
              >
                <S.LineCheckboxWrapper>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(lineId)}
                  />
                </S.LineCheckboxWrapper>
                <S.LineInfo>
                  <S.LineTitleRow isSelected={isSelected} lineColor={lineColor}>
                    <span>{line.DFNOMELINHA}</span>
                    <S.ColorPickerLabel title="Escolher cor">
                      <MdPalette size={16} />
                      <input
                        type="color"
                        value={lineColor}
                        onChange={e => onColorChange(lineId, e.target.value)}
                      />
                    </S.ColorPickerLabel>
                  </S.LineTitleRow>

                  <S.LineDetailsGrid>
                    <div>
                      <strong>Código:</strong> {line.DFIDLINHA}
                    </div>
                    <div>
                      <strong>Tanques:</strong>{' '}
                      {line.DFQUANTIDADEDETANQUES || 0}
                    </div>
                    <div>
                      <strong>Empresa:</strong>{' '}
                      {line.DFNOMEFANTASIA || 'Não informado'}
                    </div>
                  </S.LineDetailsGrid>

                  <S.InactiveToggleRow>
                    <input
                      type="checkbox"
                      id={`inactive-${line.DFIDLINHA}`}
                      checked={showInactive}
                      onChange={() => onToggleInactive(lineId)}
                    />
                    <label htmlFor={`inactive-${line.DFIDLINHA}`}>
                      Exibir inativos
                    </label>
                  </S.InactiveToggleRow>
                </S.LineInfo>
              </S.LineItem>
            );
          })
        ) : (
          <S.NoResultsMessage>
            <S.NoResultsIconContainer>
              <FiSearch size={32} />
            </S.NoResultsIconContainer>
            <span>Nenhuma linha encontrada para "{searchTerm}"</span>
          </S.NoResultsMessage>
        )}
      </S.LinesList>
    </S.SidebarContainer>
  );
};

export default SidebarLines;
